import * as Sentry from '@sentry/nuxt'

/**
 * Unified reconnection / fallback telemetry for every realtime channel.
 *
 * WHY THIS EXISTS
 * ---------------
 * The app has 8 distinct realtime / online mechanisms (one native WebSocket game
 * socket + six Supabase Realtime channels sharing one Phoenix socket + several HTTP
 * polling fallbacks). Each had its own ad-hoc (or zero) Sentry coverage, so we could
 * not answer: how long do reconnects take, how often do users hit a fallback, and
 * which fallback. This module is the single normalized reporting surface so every
 * channel emits identically-shaped events.
 *
 * SCOPE NOTE (revised 2026-08-05, #818; amended 2026-08-13): a ROUTINE trigger that
 * ends in `recovered` or `fallback` is breadcrumb-only, and so is any Supabase
 * resubscribe faster than SHORT_RECOVERY_MS. The queryable event stream therefore
 * covers failure-driven cycles that actually lasted — not every cycle.
 *
 * In practice that silences the `tab_return` fallbacks (balance / chat / price
 * tab-refetch), which are a user switching tabs rather than an outage, and the
 * sub-30s Supabase flaps that dominate mobile ad traffic. Counting "how often did
 * a fallback serve data" in Discover therefore no longer includes them; read the
 * `category:'websocket'` breadcrumbs for the full picture. Both cuts were
 * deliberate: these cycles made up the bulk of every event the org recorded,
 * against a quota that discards the overflow.
 *
 * SENTRY DEPLOYMENT CONSTRAINTS (free tier)
 * -----------------------------------------
 * `tracesSampleRate` is unset and Sentry Metrics is a paid feature, so
 * `startSpan` / `setMeasurement` / `Sentry.metrics.*` produce NO data in this
 * deployment. The only primitives that reach Sentry are:
 *   - breadcrumbs        — free, attached to any captured event, give the full trail
 *   - captureMessage     — creates one queryable issue/event, carries tags + extra
 *
 * So duration (ms) and counts are carried as indexed `tags` + searchable `extra`
 * on `captureMessage`. To protect the free-tier event quota, terminal messages are
 * THROTTLED per (channel, outcome, trigger, fallback) key — see THROTTLE_WINDOW_MS.
 * Aggregate stats (count of `fallback:rest_poll`, p95 `durationMs` by `channel`,
 * etc.) are then read in Sentry Discover by grouping on these tags.
 *
 * If/when paid tracing + Metrics are enabled, swap the body of `reportReconnect`
 * to `Sentry.metrics.distribution('ws.reconnect.duration', durationMs, { tags })`
 * + `Sentry.metrics.increment('ws.reconnect.fallback', 1, { tags })` — call sites
 * do not change.
 */

/** Which realtime mechanism the event came from. */
export type ReconnectChannel =
  | 'game' // useGameWebSocket — native WS
  | 'crash-broadcast' // useSupabaseRealtime — legacy crash-game broadcast
  | 'balance' // useBalanceRealtime
  | 'deposit' // useDepositRealtime
  | 'price' // useExchangeRate / useXautPrice
  | 'chat' // useChatSocket
  | 'liveops' // useLiveOpsSocket
  | 'presence' // usePresence — HTTP heartbeat liveness

/** Underlying transport, so native-WS vs Supabase vs HTTP issues can be separated. */
export type ReconnectTransport = 'native-ws' | 'supabase-realtime' | 'http-poll'

/** How a reconnect / disconnect cycle resolved. */
export type ReconnectOutcome =
  | 'recovered' // connection / subscription restored
  | 'fallback' // primary transport down, data served by a fallback path
  | 'exhausted' // gave up after max attempts (game socket only)
  | 'degraded' // channel errored and is NOT self-recovering (silent stale)

/** What caused the disconnect / reconnect cycle. */
export type ReconnectTrigger =
  | 'pong_timeout'
  | 'bg_pong_timeout'
  | 'code_1006' // expected server recycle
  | 'force_crash'
  | 'proactive' // wall-clock limit pre-empt
  | 'tab_return'
  | 'offline'
  | 'online'
  | 'connect_timeout'
  | 'channel_error' // Supabase CHANNEL_ERROR
  | 'timed_out' // Supabase TIMED_OUT
  | 'closed' // Supabase CLOSED
  | 'unknown'

/** Which fallback path (if any) served data during the outage. */
export type ReconnectFallback =
  | 'none'
  | 'rest_resync' // game state re-hydrated via REST (useGame crash-state)
  | 'rest_poll' // setInterval status polling (deposit / withdraw)
  | 'history_reload' // chat history re-fetched on reconnect / tab return
  | 'stale_price' // price frozen at last value / hardcoded fallback
  | 'tab_refetch' // tab-visibility-triggered REST refetch

export interface ReconnectReport {
  channel: ReconnectChannel
  transport: ReconnectTransport
  outcome: ReconnectOutcome
  trigger: ReconnectTrigger
  /** Reconnect / resubscribe attempts in this cycle (0 if not tracked). */
  attempts?: number
  /** Time from disconnect to recovery in ms — THE reconnect time. */
  durationMs?: number
  /** Which fallback served data during the outage. */
  fallback?: ReconnectFallback
  /** Any extra context (close code, reason, error message, hidden duration…). */
  extra?: Record<string, unknown>
}

// Throttle terminal captureMessage calls to protect the event quota.
const THROTTLE_WINDOW_MS = 60_000

/**
 * A Supabase channel that resubscribes faster than this is treated as a flap
 * rather than an outage — see trackSupabaseChannelStatus. Chosen from observed
 * production data: real outages sit well above this, while the mobile-ad-traffic
 * flaps that dominate the volume recover in a few seconds.
 */
const SHORT_RECOVERY_MS = 30_000

const lastReportedAt = new Map<string, number>()

/** Routine, expected triggers. A reconnect driven by one of these is scheduled
 *  behaviour, not an incident: the game socket recycles every 350s because
 *  Supabase Edge Functions cap a worker at 400s wall-clock, and tab-return /
 *  online are the user switching context. When such a cycle *succeeds* — or is
 *  served by a fallback — it stays breadcrumb-only (see reportReconnect). */
const ROUTINE_TRIGGERS = new Set<ReconnectTrigger>([
  'proactive',
  'code_1006',
  'force_crash',
  'tab_return',
  'online',
])

function outcomeLevel(outcome: ReconnectOutcome): Sentry.SeverityLevel {
  switch (outcome) {
    case 'recovered':
      return 'info'
    case 'fallback':
    case 'degraded':
      return 'warning'
    case 'exhausted':
      return 'error'
  }
}

/**
 * Emit a `category:'websocket'` breadcrumb. Free, no issue created. Use at every
 * step of a reconnect cycle so the trail is visible on any later captured event.
 */
export function reconnectBreadcrumb(
  channel: ReconnectChannel,
  message: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({
    category: 'websocket',
    message: `[${channel}] ${message}`,
    level,
    data,
  })
}

/**
 * Report a resolved reconnect / fallback cycle.
 *
 * Always emits a breadcrumb. Emits a throttled `captureMessage` carrying the
 * duration, attempts and fallback as indexed tags + extra, so Sentry Discover can
 * count fallback hits and chart reconnect duration per channel — EXCEPT when a
 * routine trigger simply recovered or fell back, which is scheduled behaviour and
 * stays on the breadcrumb trail only. See ROUTINE_TRIGGERS.
 *
 * @returns the Sentry event id if a message was sent, or null if it was routine
 *          or throttled.
 */
export function reportReconnect(report: ReconnectReport): string | null {
  const {
    channel,
    transport,
    outcome,
    trigger,
    attempts = 0,
    durationMs,
    fallback = 'none',
    extra = {},
  } = report

  const level = outcomeLevel(outcome)

  // Always leave a breadcrumb trail (free).
  reconnectBreadcrumb(channel, `reconnect ${outcome} (${trigger})`, level, {
    transport,
    attempts,
    durationMs,
    fallback,
    ...extra,
  })

  // A routine cycle that resolved itself is not an issue — reporting it says
  // only "the thing that must happen, happened, and it worked". The breadcrumb
  // above still carries the duration onto any real error captured later, which
  // is where it is actually useful. Emitting these was ~85% of every event we
  // managed to record org-wide, and the quota it burned is why genuine errors
  // elsewhere were being dropped.
  if (ROUTINE_TRIGGERS.has(trigger) && (outcome === 'recovered' || outcome === 'fallback')) {
    return null
  }

  // Throttle the queryable message per distinct signature.
  const key = `${channel}:${outcome}:${trigger}:${fallback}`
  const now = Date.now()
  const last = lastReportedAt.get(key)

  if (last !== undefined && now - last < THROTTLE_WINDOW_MS) {
    return null
  }
  lastReportedAt.set(key, now)

  return Sentry.captureMessage(`ws.reconnect.${outcome}`, {
    level,
    tags: {
      flow: 'websocket',
      channel,
      transport,
      trigger,
      outcome,
      fallback,
    },
    extra: {
      attempts,
      durationMs,
      ...extra,
    },
  })
}

// =============================================================================
// SUPABASE REALTIME HELPERS
// =============================================================================

/** Per-channel degradation tracking: when a Supabase channel last entered an
 *  error/timeout state, so a later SUBSCRIBED can be reported as a recovery with
 *  its resubscribe duration. */
const degradedSince = new Map<ReconnectChannel, { at: number, trigger: ReconnectTrigger }>()

/**
 * Normalized telemetry for a Supabase Realtime channel `.subscribe()` status
 * callback. Tracks the degradation→recovery transition and reports the
 * resubscribe duration. TELEMETRY ONLY — does not change channel behavior.
 *
 * Supabase-js auto-rejoins the underlying socket internally, so a `SUBSCRIBED`
 * after a `CHANNEL_ERROR`/`TIMED_OUT` is an implicit recovery we can time.
 * `CLOSED` is intentionally treated as a breadcrumb (it also fires on the normal
 * `removeChannel()` teardown, so it must not be counted as an outage).
 *
 * @param channel  which logical channel this is
 * @param status   the raw Supabase status string (SUBSCRIBED | CHANNEL_ERROR | TIMED_OUT | CLOSED)
 * @param errorMessage optional error message from the status callback
 */
export function trackSupabaseChannelStatus(
  channel: ReconnectChannel,
  status: string,
  errorMessage?: string,
): void {
  if (status === 'SUBSCRIBED') {
    const prev = degradedSince.get(channel)
    if (prev) {
      degradedSince.delete(channel)
      // Clamped: a backwards clock step (an NTP correction mid-session is the
      // realistic trigger on mobile) would otherwise produce a negative duration,
      // which satisfies the flap check below and would silently discard a long
      // outage. Clamping makes the classification explicit rather than incidental.
      const durationMs = Math.max(0, Date.now() - prev.at)

      // A channel that came back on its own within a few seconds is a flap, not
      // an outage: supabase-js rejoined before the user could notice, and the
      // matching `degraded` event has already recorded that it happened at all.
      // Reporting the recovery too doubles the cost of every flap for no added
      // signal — and on mobile ad traffic these flap constantly (measured: ~7K
      // recoveries a week, median a few seconds, against ~130 users).
      if (durationMs < SHORT_RECOVERY_MS) {
        // Same 'reconnect ' prefix and payload shape as the reported path, so a
        // breadcrumb trail can be scanned uniformly — this is now the dominant
        // case and must not be the one that reads differently.
        reconnectBreadcrumb(channel, `reconnect recovered quickly (${prev.trigger})`, 'info', {
          transport: 'supabase-realtime',
          attempts: 0,
          durationMs,
          fallback: 'none',
        })
        return
      }

      reportReconnect({
        channel,
        transport: 'supabase-realtime',
        outcome: 'recovered',
        trigger: prev.trigger,
        durationMs,
      })
    }
    return
  }

  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
    const trigger: ReconnectTrigger = status === 'TIMED_OUT' ? 'timed_out' : 'channel_error'

    // Latch on the healthy→degraded edge. supabase-js keeps re-firing this
    // callback while a channel stays broken, so reporting every call meant one
    // event per throttle window per channel, for as long as the outage lasted —
    // across 6 channels. The transition is the signal; the repeats are volume.
    if (degradedSince.has(channel)) {
      reconnectBreadcrumb(
        channel,
        `still degraded (${trigger})`,
        'warning',
        errorMessage ? { error: errorMessage } : undefined,
      )
      return
    }

    degradedSince.set(channel, { at: Date.now(), trigger })
    reportReconnect({
      channel,
      transport: 'supabase-realtime',
      outcome: 'degraded',
      trigger,
      extra: errorMessage ? { error: errorMessage } : undefined,
    })
    return
  }

  if (status === 'CLOSED') {
    // Release the latch. CLOSED is the normal `removeChannel()` teardown, so a
    // channel that errored and was then torn down would otherwise stay marked
    // degraded for the life of the page — and its next, genuinely separate
    // outage would never be reported. All six channels remount on route churn.
    degradedSince.delete(channel)
    reconnectBreadcrumb(channel, 'channel closed', 'info')
  }
}

/**
 * Report that a fallback path served data during an outage (e.g. tab-visibility
 * REST refetch, deposit/withdraw status polling, game-state REST resync).
 * This is what makes "how many times users hit fallback X" countable.
 */
export function reportFallback(
  channel: ReconnectChannel,
  fallback: ReconnectFallback,
  trigger: ReconnectTrigger = 'unknown',
  extra?: Record<string, unknown>,
): string | null {
  return reportReconnect({
    channel,
    transport: channel === 'game' ? 'native-ws' : 'supabase-realtime',
    outcome: 'fallback',
    trigger,
    fallback,
    extra,
  })
}

/** Test-only — reset all internal state between cases. */
export function __resetReconnectTelemetryForTests(): void {
  lastReportedAt.clear()
  degradedSince.clear()
}
